import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileCheck, ShieldCheck, Users, FileText, CalendarCheck, ArrowRight, Star } from "lucide-react";
import api from "../lib/api";

const HERO_PHOTO =
  "https://images.unsplash.com/photo-1514306191717-452ec28c7814?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxjb25jZXJ0JTIwc3RhZ2UlMjByZWQlMjBjdXJ0YWluJTIwZHJhbWF0aWMlMjBzcG90bGlnaHR8ZW58MHx8fHwxNzkwNjA5OTk2fDA&ixlib=rb-4.1.0&q=85";

const BAND_PHOTO =
  "https://images.unsplash.com/photo-1565035010268-a3816f98589a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxsaXZlJTIwbXVzaWMlMjBwZXJmb3JtYW5jZSUyMHN0YWdlJTIwc2lsaG91ZXR0ZSUyMGxpZ2h0c3xlbnwwfHx8fDE3OTA2MDk5OTZ8MA&ixlib=rb-4.1.0&q=85";

function Hero({ iscritti }) {
  return (
    <section className="relative overflow-hidden border-b border-gold/20 bg-[#3A0E1B]">
      <div className="pointer-events-none absolute inset-0 grain" aria-hidden="true" />
      <div className="pointer-events-none absolute left-6 top-6 h-16 w-16 border-l-2 border-t-2 border-gold/30" aria-hidden="true" />
      <div className="pointer-events-none absolute bottom-6 right-6 h-16 w-16 border-b-2 border-r-2 border-gold/30" aria-hidden="true" />
      <div className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 py-16 sm:px-6 sm:py-24 lg:grid-cols-12 lg:gap-8 lg:px-8">
        <div className="flex flex-col justify-center gap-6 lg:col-span-7 lg:pr-6">
          <div
            data-testid="counter-registered-members"
            className="inline-flex w-fit items-center gap-2 rounded-full border border-gold/40 bg-[#571C2E] px-4 py-2 text-sm font-semibold tracking-wide text-gold"
          >
            <Users className="h-4 w-4" />
            ISCRITTI ALL'ASSOCIAZIONE: {iscritti.toLocaleString("it-IT")}
          </div>
          <h1 className="font-heading text-4xl font-bold leading-[1.08] tracking-tight text-[#FFFDF7] sm:text-5xl lg:text-6xl">
            Tu pensa alla musica.
            <span className="block italic text-gold">Alla burocrazia pensiamo noi.</span>
          </h1>
          <p className="max-w-xl text-base leading-relaxed text-[#E8DED1] sm:text-lg">
            ENPALSBOX offre assistenza agli artisti e ai lavoratori dello spettacolo per gli adempimenti
            previdenziali collegati alle loro attività: certificato di agibilità, F24 e dichiarazione mensile,
            in modo semplice e affidabile.
          </p>
          <div className="flex flex-wrap gap-3 pt-2">
            <Link to="/iscriviti" data-testid="hero-register-button" className="rounded-xl bg-gradient-to-r from-gold to-[#F7E5A9] px-8 py-4 text-sm font-bold uppercase tracking-wider text-[#1F040A] shadow-[0_4px_20px_rgba(212,175,55,0.35)] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_28px_rgba(212,175,55,0.45)]">
              ISCRIVITI
            </Link>
            <Link to="/accedi" data-testid="hero-login-button" className="rounded-xl border border-gold/50 bg-[#632035] px-8 py-4 text-sm font-semibold uppercase tracking-wider text-[#FFFDF7] transition-colors duration-300 hover:bg-[#6E2138]">
              ACCEDI
            </Link>
            <Link to="/richiedi-agibilita" data-testid="hero-request-certificate-button" className="rounded-xl border border-gold/30 px-6 py-3.5 text-xs font-semibold uppercase tracking-wider text-[#F7E5A9] transition-colors duration-300 hover:border-gold sm:text-sm">
              RICHIEDI CERTIFICATO DI AGIBILITÀ
            </Link>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 pt-1 text-sm">
            <Link to="/certificato-agibilita" data-testid="hero-what-is-certificate-link" className="inline-flex items-center gap-1 font-medium text-[#B5A797] transition-colors hover:text-gold">
              Cos'è il certificato di agibilità <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <Link to="/f24" data-testid="hero-f24-link" className="inline-flex items-center gap-1 font-medium text-[#B5A797] transition-colors hover:text-gold">
              F24 <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <Link to="/dichiarazione-mensile" data-testid="hero-dichiarazione-link" className="inline-flex items-center gap-1 font-medium text-[#B5A797] transition-colors hover:text-gold">
              Dichiarazione mensile <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <Link to="/contatti" data-testid="hero-contact-link" className="inline-flex items-center gap-1 font-medium text-[#B5A797] transition-colors hover:text-gold">
              Contattaci <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
        <div className="relative flex items-center justify-center lg:col-span-5">
          <div className="rounded-2xl border-2 border-gold/40 bg-[#4A1424]/90 p-2 shadow-[0_20px_50px_rgba(0,0,0,0.8)] outline outline-1 outline-gold/20" data-testid="hero-photo-frame">
            <div className="relative overflow-hidden rounded-xl">
              <img src={HERO_PHOTO} alt="Sipario rosso di un teatro prima dello spettacolo" className="max-h-[520px] w-full object-cover contrast-125" />
              <div className="absolute inset-0 bg-bordeaux/25 mix-blend-multiply" aria-hidden="true" />
            </div>
          </div>
          <div className="absolute -bottom-5 left-0 rounded-xl border border-gold/40 bg-[#571C2E] px-6 py-4 text-center shadow-lg sm:-left-4" data-testid="hero-stat-card">
            <p className="font-heading text-3xl font-bold text-gold">{iscritti.toLocaleString("it-IT")}+</p>
            <p className="mt-1 text-[11px] font-semibold uppercase tracking-widest text-[#E8DED1]">Artisti e DJ registrati</p>
          </div>
        </div>
      </div>
    </section>
  );
}

const SERVIZI = [
  {
    icon: FileCheck,
    testid: "service-card-agibilita",
    title: "Certificato di Agibilità",
    text: "Richiedi il certificato di agibilità per le tue esibizioni direttamente online: compili il modulo, noi gestiamo la pratica.",
  },
  {
    icon: FileText,
    testid: "service-card-f24",
    title: "F24",
    text: "Informazioni chiare sul modello F24 e sul suo collegamento con gli adempimenti previdenziali e fiscali dello spettacolo.",
  },
  {
    icon: CalendarCheck,
    testid: "service-card-dichiarazione",
    title: "Dichiarazione Mensile",
    text: "Ti spieghiamo cos'è la dichiarazione mensile e perché è importante per le tue prestazioni artistiche.",
  },
];

function OperativoBanner() {
  return (
    <section className="relative overflow-hidden border-y border-gold/25 bg-[#571C2E] px-4 py-12 sm:px-6 lg:px-8" data-testid="sempre-operativo-banner">
      <div className="pointer-events-none absolute inset-0 grain" aria-hidden="true" />
      <div className="relative z-10 mx-auto flex max-w-7xl flex-col items-center gap-4 text-center">
        <p className="kicker justify-center">Sempre al tuo fianco</p>
        <p className="max-w-3xl font-heading text-2xl font-semibold leading-snug text-[#FFFDF7] sm:text-3xl">
          Hai bisogno di un'agibilità <span className="italic text-gold">all'ultimo minuto</span>, nel weekend o durante le feste?
        </p>
        <p className="max-w-2xl text-sm leading-relaxed text-[#E8DED1] sm:text-base">
          Non preoccuparti: ENPALSBOX è <span className="font-semibold text-[#F7E5A9]">sempre operativo</span>.
        </p>
      </div>
    </section>
  );
}

function ServiziSection() {
  return (
    <section className="bg-[#3A0E1B] px-4 py-20 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <p className="kicker">I nostri servizi</p>
        <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight text-[#FFFDF7] sm:text-4xl" data-testid="home-services-title">Cosa facciamo per te</h2>
        <p className="mt-3 max-w-2xl text-[#B5A797]">
          Ci occupiamo degli adempimenti previdenziali legati alla tua attività artistica, così puoi concentrarti sul palco.
        </p>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SERVIZI.map((s) => (
            <div key={s.testid} className="relative overflow-hidden rounded-xl border border-gold/30 bg-[#4A1424] p-8 shadow-xl ring-1 ring-inset ring-gold/10 transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:border-gold/60 hover:shadow-[0_16px_40px_rgba(212,175,55,0.12)]" data-testid={s.testid}>
              <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-gold to-gold/20" aria-hidden="true" />
              <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-gold/40 bg-[#632035] text-gold">
                <s.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-4 font-heading text-2xl font-semibold text-[#F7E5A9]">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#E8DED1]">{s.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
const TARIFFE = [
  {
    testid: "price-card-servizio",
    title: "Servizio",
    prezzo: "30",
    nota: "a pratica",
    text: "Costo del servizio per la gestione della tua richiesta di certificato di agibilità.",
  },
  {
    testid: "price-card-contributi",
    title: "Contributi",
    prezzo: "21",
    nota: "a pratica",
    text: "Contributi previdenziali collegati alla tua esibizione, versati secondo la normativa.",
  },
  {
    testid: "price-card-iscrizione",
    title: "Iscrizione all'associazione",
    prezzo: "15",
    nota: "una volta l'anno",
    text: "Quota associativa annuale: si paga una sola volta all'anno e copre tutti i servizi ENPALSBOX.",
  },
];

function TariffeSection() {
  return (
    <section className="border-t border-gold/20 bg-[#3A0E1B] px-4 py-20 sm:px-6 lg:px-8" data-testid="tariffe-section">
      <div className="mx-auto max-w-7xl">
        <p className="kicker">Tariffe chiare</p>
        <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight text-[#FFFDF7] sm:text-4xl" data-testid="tariffe-title">Quanto costa</h2>
        <p className="mt-3 max-w-2xl text-[#B5A797]">
          Nessun costo nascosto: ecco tutte le voci di spesa, semplici e trasparenti.
        </p>
        <div className="mt-10 grid gap-6 sm:grid-cols-3">
          {TARIFFE.map((t) => (
            <div key={t.testid} className="relative overflow-hidden rounded-xl border border-gold/30 bg-[#4A1424] p-8 text-center shadow-xl ring-1 ring-inset ring-gold/10 transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:border-gold/60 hover:shadow-[0_16px_40px_rgba(212,175,55,0.12)]" data-testid={t.testid}>
              <div className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-gold/20 via-gold to-gold/20" aria-hidden="true" />
              <h3 className="font-heading text-xl font-semibold text-[#F7E5A9]">{t.title}</h3>
              <p className="mt-4 font-heading text-5xl font-bold text-gold">
                {t.prezzo}<span className="text-2xl">€</span>
              </p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-widest text-[#E8DED1]">{t.nota}</p>
              <p className="mt-4 text-sm leading-relaxed text-[#E8DED1]">{t.text}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 rounded-xl border border-gold/40 bg-gradient-to-r from-[#571C2E] via-[#632035] to-[#571C2E] p-6 text-center ring-1 ring-inset ring-gold/15 sm:p-8" data-testid="calcolatore-home-cta">
          <p className="kicker justify-center">Strumento gratuito</p>
          <p className="mt-3 font-heading text-xl font-semibold text-[#FFFDF7] sm:text-2xl">
            Quanto ti resta in tasca dopo i contributi?
          </p>
          <p className="mx-auto mt-2 max-w-xl text-sm text-[#E8DED1]">
            Prova il nostro calcolatore interattivo: inserisci il compenso lordo e scopri subito la stima di contributi e netto.
          </p>
          <Link to="/calcolatore" data-testid="calcolatore-home-button" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-gold to-[#F7E5A9] px-8 py-3 text-sm font-bold uppercase tracking-wider text-[#1F040A] shadow-[0_4px_20px_rgba(212,175,55,0.35)] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_28px_rgba(212,175,55,0.45)]">
            APRI IL CALCOLATORE <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-10 rounded-xl border border-gold/30 bg-[#571C2E] p-6 sm:p-8" data-testid="unilav-note">
          <p className="kicker">Sei esente?</p>
          <p className="mt-3 text-sm leading-relaxed text-[#E8DED1] sm:text-base">
            Se sei già <span className="font-semibold text-[#FFFDF7]">dipendente</span> o <span className="font-semibold text-[#FFFDF7]">pensionato</span> puoi essere esente dagli adempimenti ENPALS:
            in questo caso puoi richiedere la comunicazione <span className="font-semibold text-[#F7E5A9]">UniLAV</span> scrivendoci direttamente via email a{" "}
            <a href="mailto:enpalsbox@enpalsbox.it" data-testid="unilav-email-link" className="font-semibold text-gold underline underline-offset-4 transition-colors hover:text-[#F7E5A9]">
              enpalsbox@enpalsbox.it
            </a>
            . Ti risponderemo con tutte le indicazioni necessarie.
          </p>
        </div>
      </div>
    </section>
  );
}



function EditorialBand() {
  return (
    <section className="relative overflow-hidden border-y border-gold/25" data-testid="editorial-band">
      <img src={BAND_PHOTO} alt="Pubblico a un concerto dal vivo sotto le luci dorate del palco" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-[#3A0E1B]/80" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0 grain" aria-hidden="true" />
      <div className="relative z-10 mx-auto flex max-w-4xl flex-col items-center px-4 py-24 text-center sm:px-6 sm:py-28">
        <p className="kicker justify-center">Dal vivo, sempre</p>
        <p className="mt-4 font-heading text-3xl font-bold italic leading-snug text-[#FFFDF7] sm:text-4xl">
          Il palco è tuo.
          <span className="block text-gold">Alla burocrazia pensiamo noi.</span>
        </p>
        <Link to="/iscriviti" data-testid="editorial-cta-button" className="mt-8 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-gold to-[#F7E5A9] px-8 py-3.5 text-sm font-bold uppercase tracking-wider text-[#1F040A] shadow-[0_4px_20px_rgba(212,175,55,0.35)] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_28px_rgba(212,175,55,0.45)]">
          UNISCITI A NOI <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </section>
  );
}

const STEPS = [
  { n: "1", t: "Iscriviti", d: "Crea il tuo account in pochi minuti con i tuoi dati e il tuo Codice Fiscale." },
  { n: "2", t: "Invia la richiesta", d: "Compila il modulo con i dati del locale, la data dell'esibizione e l'importo pattuito." },
  { n: "3", t: "Segui la pratica", d: "Ricevi la conferma via email e monitora lo stato dalla tua Area Personale." },
];

function StepsSection() {
  return (
    <section className="border-t border-gold/20 bg-[#3A0E1B] py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <p className="kicker">Semplice, in tre mosse</p>
        <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight text-[#FFFDF7] sm:text-4xl" data-testid="home-steps-title">Come funziona</h2>
        <div className="mt-10 grid gap-6 sm:grid-cols-3">
          {STEPS.map((s) => (
            <div key={s.n} className="rounded-xl border border-gold/30 bg-[#4A1424] p-8 shadow-xl ring-1 ring-inset ring-gold/10 transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:border-gold/60 hover:shadow-[0_16px_40px_rgba(212,175,55,0.12)]" data-testid={`step-card-${s.n}`}>
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-gold to-[#B89428] font-heading text-lg font-bold text-[#1F040A] shadow-[0_4px_16px_rgba(212,175,55,0.35)]">
                {s.n}
              </span>
              <h3 className="mt-4 font-heading text-xl font-semibold text-[#F7E5A9]">{s.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#E8DED1]">{s.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap items-center gap-4">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-[#571C2E] px-5 py-2.5 text-sm font-bold text-gold" data-testid="home-members-highlight">
            <ShieldCheck className="h-4 w-4" />
            OLTRE 1.000 ARTISTI ISCRITTI
          </div>
          <Link to="/iscriviti" data-testid="home-cta-register" className="rounded-xl bg-gradient-to-r from-gold to-[#F7E5A9] px-8 py-3 text-sm font-bold uppercase tracking-wider text-[#1F040A] shadow-[0_4px_20px_rgba(212,175,55,0.35)] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_28px_rgba(212,175,55,0.45)]">
            ISCRIVITI ORA
          </Link>
        </div>
      </div>
    </section>
  );
}

const TESTIMONIANZE = [
  {
    ruolo: "Cantante",
    testo: "Finalmente qualcuno che si occupa della burocrazia mentre io penso alla musica. Richiesta semplice e assistenza sempre presente.",
  },
  {
    ruolo: "Musicista",
    testo: "Ho richiesto il certificato di agibilità in pochi minuti. Tutto chiaro, veloce e professionale.",
  },
  {
    ruolo: "D.J.",
    testo: "Servizio serio e affidabile. Mi hanno seguito passo passo per gli adempimenti delle mie serate.",
  },
  {
    ruolo: "Artista iscritto",
    testo: "Piattaforma chiara e facile da usare, anche da chi non è pratico di questioni previdenziali. Consigliata.",
  },
];

function TestimonianzeSection() {
  return (
    <section className="border-t border-gold/20 bg-[#3A0E1B] px-4 py-20 sm:px-6 lg:px-8" data-testid="testimonianze-section">
      <div className="mx-auto max-w-7xl">
        <p className="kicker">Recensioni</p>
        <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight text-[#FFFDF7] sm:text-4xl" data-testid="testimonianze-title">Cosa dicono gli artisti di ENPALSBOX</h2>
        <p className="mt-3 max-w-2xl text-[#B5A797]">
          Le opinioni di chi usa la piattaforma, pubblicate in forma anonima nel rispetto della privacy.
        </p>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {TESTIMONIANZE.map((t, i) => (
            <figure key={i} className="relative flex flex-col rounded-xl border border-gold/25 bg-[#4A1424] p-8 shadow-xl transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:border-gold/50 hover:shadow-[0_16px_40px_rgba(212,175,55,0.12)]" data-testid={`testimonianza-${i + 1}`}>
              <span className="pointer-events-none absolute right-5 top-3 font-heading text-6xl leading-none text-gold/25" aria-hidden="true">”</span>
              <div className="flex gap-0.5" aria-label="5 stelle">
                {[...Array(5)].map((_, s) => (
                  <Star key={s} className="h-4 w-4 fill-gold text-gold" />
                ))}
              </div>
              <blockquote className="mt-4 flex-1 font-heading text-lg italic leading-relaxed text-[#E8DED1]">
                “{t.testo}”
              </blockquote>
              <figcaption className="mt-4 text-xs font-semibold uppercase tracking-widest text-gold">
                {t.ruolo}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [target, setTarget] = useState(null);
  const [iscritti, setIscritti] = useState(0);

  useEffect(() => {
    api.get("/public/contatore")
      .then((res) => setTarget(res.data.iscritti))
      .catch(() => setTarget(1020));
  }, []);

  useEffect(() => {
    if (target === null) return;
    const duration = 2000;
    const start = performance.now();
    let raf;
    const step = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setIscritti(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target]);

  return (
    <div data-testid="home-page" className="bg-[#3A0E1B]">
      <Hero iscritti={iscritti} />
      <OperativoBanner />
      <ServiziSection />
      <TariffeSection />
      <TestimonianzeSection />
      <EditorialBand />
      <StepsSection />
    </div>
  );
}
